import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { cn } from '@h2/ui';
import { useSidebarStore } from '../../stores/useSidebarStore';
import Icon from '../ui/icon';
import logoImage from '../../assets/logo.png';
import signatureImage from '../../assets/signature.png';

interface SidebarProps {
  className?: string;
}

const Sidebar: React.FC<SidebarProps> = ({ className }) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const {
    sidebarState,
    menuItems,
    activeMenu,
    setActiveMenu,
    expandSidebar,
    collapseSidebar,
    hideSidebar,
  } = useSidebarStore();

  const [hoveredMenu, setHoveredMenu] = useState<string | null>(null);
  const [submenuPosition, setSubmenuPosition] = useState<{ top: number; left: number } | null>(null);
  const [isSubmenuHovered, setIsSubmenuHovered] = useState(false);
  const hideTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  // URL에 따라 활성 메뉴 업데이트
  useEffect(() => {
    const path = location.pathname;
    
    // 모든 메뉴 아이템을 검색하여 현재 path와 일치하는 것을 찾음
    const findActiveMenu = (items: typeof menuItems): string | null => {
      for (const item of items) {
        if (item.path === path) {
          return item.id;
        }
        if (item.children) {
          for (const child of item.children) {
            if (child.path === path) {
              return child.id;
            }
          }
        }
      }
      return null;
    };

    const activeMenuId = findActiveMenu(menuItems);
    if (activeMenuId && activeMenuId !== activeMenu) {
      setActiveMenu(activeMenuId);
    }
  }, [location.pathname, menuItems, activeMenu, setActiveMenu]);

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, []);

  const isExpanded = sidebarState === 'expanded';
  const isCollapsed = sidebarState === 'collapsed';
  const isHidden = sidebarState === 'hidden';

  const getSidebarWidth = () => {
    if (isHidden) return 'w-0';
    if (isCollapsed) return 'w-16';
    return 'w-44';
  };

  const handleMenuClick = (menuId: string) => {
    const menuItem = menuItems.find(item => item.id === menuId);
    if (!menuItem) {
      // 자식 메뉴에서 찾기
      for (const item of menuItems) {
        if (item.children) {
          const childItem = item.children.find(child => child.id === menuId);
          if (childItem?.path) {
            navigate(childItem.path);
            return;
          }
        }
      }
      return;
    }

    if (menuItem.path) {
      navigate(menuItem.path);
    }
  };

  const handleMenuHover = (menuId: string, event: React.MouseEvent<HTMLButtonElement>) => {
    const menuItem = menuItems.find(item => item.id === menuId);
    if (menuItem?.children && menuItem.children.length > 0) {
      // Clear any existing hide timeout
      clearHideTimeout();
      
      const rect = event.currentTarget.getBoundingClientRect();
      setHoveredMenu(menuId);
      setSubmenuPosition({
        top: rect.top,
        left: rect.right + 8, // 8px gap from sidebar
      });
      setIsSubmenuHovered(false); // Reset submenu hover state
    }
  };

  const clearHideTimeout = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  };

  const hideSubmenu = () => {
    setHoveredMenu(null);
    setSubmenuPosition(null);
    setIsSubmenuHovered(false);
  };

  const handleMenuLeave = () => {
    // Clear any existing timeout
    clearHideTimeout();
    
    // Set a delay before hiding submenu to allow moving to submenu
    hideTimeoutRef.current = setTimeout(() => {
      if (!isSubmenuHovered) {
        hideSubmenu();
      }
    }, 150);
  };

  const handleSubmenuHover = () => {
    // Clear hide timeout and mark submenu as hovered
    clearHideTimeout();
    setIsSubmenuHovered(true);
  };

  const handleSubmenuLeave = () => {
    setIsSubmenuHovered(false);
    // Small delay to allow re-entering
    hideTimeoutRef.current = setTimeout(() => {
      hideSubmenu();
    }, 100);
  };

  if (isHidden) {
    return (
      <div className="fixed top-4 left-4 z-50">
        <button
          onClick={expandSidebar}
          className="p-2 bg-gray-800 text-white rounded-md hover:bg-gray-700 transition-colors"
        >
          <Icon name="Menu" size={20} />
        </button>
      </div>
    );
  }

  return (
    <>
      <div
        className={cn(
          'bg-gray-900 text-white transition-all duration-300 ease-in-out flex flex-col border-r border-gray-800',
          getSidebarWidth(),
          'h-screen overflow-hidden relative',
          isCollapsed && 'overflow-x-hidden',
          className
        )}
      >
      {/* Header */}
      <div className="group relative flex items-center justify-start py-2 px-4 border-b border-gray-800">
        {isExpanded && (
          <>
            <img 
              src={signatureImage} 
              alt="Voithru Signature" 
              className="h-14 object-contain scale-150"
            />
            <div className="absolute right-3 flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
              <button
                onClick={collapseSidebar}
                className="p-1 hover:bg-gray-800 rounded transition-colors"
                title="Collapse sidebar"
              >
                <Icon name="Minus" size={16} />
              </button>
              <button
                onClick={hideSidebar}
                className="p-1 hover:bg-gray-800 rounded transition-colors"
                title="Hide sidebar"
              >
                <Icon name="X" size={16} />
              </button>
            </div>
          </>
        )}
        
        {isCollapsed && (
          <>
            <img 
              src={logoImage} 
              alt="Voithru Logo" 
              className="w-10 h-10 object-contain scale-[2]"
            />
            <button
              onClick={expandSidebar}
              className="absolute right-1 p-1 hover:bg-gray-800 rounded transition-colors opacity-0 group-hover:opacity-100 transition-opacity duration-200"
              title="Expand sidebar"
            >
              <Icon name="ChevronRight" size={16} />
            </button>
          </>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-3">
        <ul className="space-y-0.5">
          {menuItems.map((item) => {
            const isActive = activeMenu === item.id;
            const hasChildren = Boolean(item.children && item.children.length > 0);

            return (
              <li key={item.id}>
                {/* Main menu item */}
                <button
                  onClick={() => handleMenuClick(item.id)}
                  onMouseEnter={(e) => handleMenuHover(item.id, e)}
                  onMouseLeave={handleMenuLeave}
                  className={cn(
                    'w-full flex items-center space-x-2 pl-4 pr-2 py-2 text-left hover:bg-gray-800 transition-colors',
                    isActive && 'bg-gray-800 border-r-2 border-blue-500',
                    isCollapsed && 'justify-center px-2'
                  )}
                  title={isCollapsed ? item.label : undefined}
                >
                  <Icon 
                    name={item.icon} 
                    size={18} 
                    className={cn(
                      'flex-shrink-0',
                      isActive ? 'text-blue-400' : 'text-gray-400'
                    )} 
                  />
                  
                  {isExpanded && (
                    <>
                      <span className="flex-1 font-medium text-sm whitespace-nowrap transition-opacity duration-300 delay-300">{item.label}</span>
                      {hasChildren && (
                        <Icon
                          name="ChevronRight"
                          size={14}
                          className="text-gray-400 transition-opacity duration-300 delay-300"
                        />
                      )}
                    </>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Footer */}
      {isExpanded && (
        <div className="p-3 border-t border-gray-800">
          <div className="flex items-center space-x-2">
            <div className="w-7 h-7 bg-gray-700 rounded-full flex items-center justify-center">
              <Icon name="Users" size={14} className="text-gray-300" />
            </div>
            <div className="flex-1 overflow-hidden">
              <div className="text-xs font-medium whitespace-nowrap transition-opacity duration-300 delay-300">Admin User</div>
              <div className="text-xs text-gray-400 whitespace-nowrap transition-opacity duration-300 delay-300">admin@example.com</div>
            </div>
          </div>
        </div>
      )}

      {isCollapsed && (
        <div className="p-3 border-t border-gray-800 flex justify-center">
          <div className="w-7 h-7 bg-gray-700 rounded-full flex items-center justify-center">
            <Icon name="Users" size={14} className="text-gray-300" />
          </div>
        </div>
      )}
      </div>

      {/* Floating Submenu Panel */}
      {hoveredMenu && submenuPosition && (() => {
        const menuItem = menuItems.find(item => item.id === hoveredMenu);
        return menuItem?.children && menuItem.children.length > 0 ? (
          <div
            className="submenu-panel fixed z-50 bg-gray-800 border border-gray-700 rounded-lg shadow-xl min-w-[180px] py-2"
            style={{
              top: submenuPosition.top,
              left: submenuPosition.left,
            }}
            onMouseEnter={handleSubmenuHover}
            onMouseLeave={handleSubmenuLeave}
          >
            {/* Submenu Header */}
            <div className="px-3 py-2 border-b border-gray-700">
              <div className="flex items-center space-x-2">
                <Icon 
                  name={menuItem.icon} 
                  size={14} 
                  className="text-blue-400" 
                />
                <span className="font-medium text-sm text-white">{menuItem.label}</span>
              </div>
            </div>
            
            {/* Submenu Items */}
            <div className="py-1">
              {menuItem.children.map((child) => (
                <button
                  key={child.id}
                  onClick={() => {
                    if (child.path) {
                      navigate(child.path);
                    }
                    setHoveredMenu(null);
                    setSubmenuPosition(null);
                  }}
                  className={cn(
                    'w-full flex items-center space-x-2 px-3 py-2 text-left text-sm hover:bg-gray-700 transition-colors text-gray-300',
                    activeMenu === child.id && 'bg-gray-700 text-blue-400'
                  )}
                >
                  <Icon 
                    name={child.icon} 
                    size={14} 
                    className="flex-shrink-0" 
                  />
                  <span className="font-medium text-xs">{child.label}</span>
                </button>
              ))}
            </div>
          </div>
        ) : null;
      })()}
    </>
  );
};

export default Sidebar;
